import type { DealChain } from './dealChain';
import type { ApiChainToken } from 'src/types/chain';
import type { ApiDealStepEvent } from 'src/types/dealStep';
import type { ApiDeliveryDealDetail } from 'src/types/delivery';

import { DealStep } from 'src/types/dealStep';
import { DeliveryDealStatus } from 'src/types/delivery';

import { formatUtcDay, formatDateTime } from './date';
import { formatAmount, formatBaseUnits } from './money';

/**
 * The delivery tracker's model (contracts v3, UI part §3) — a postal tracker, not a state dump.
 *
 * Written as pure functions over what api recorded, because everything the panel promises is a
 * fact somebody else established: a step is "done" because an event exists for it, its time is
 * the time of that event (block time on chain, server time off it), and a money line reads the
 * amount out of the event's own `details` rather than recomputing it from the deal. Nothing here
 * derives a date from a status — the reason `deal_step_events` exists at all.
 *
 * Two guidelines drive the shape (NN/g "Status Trackers", Baymard order-tracking):
 *
 * 1. **A tracker without an expected date is the top cause of distrust**, so the header always
 *    carries one — the logist's delivery day, the deadline of the open window, or the plain
 *    statement that we are waiting for a signature.
 * 2. **Few big steps, detail inside them.** Six milestones carry the story; hashes, amounts and
 *    the second act of a dispute live as notes under the step they belong to.
 *
 * And one rule of ours: no silent money. Every movement is a note with an amount and, when a
 * transaction carried it, a link to that transaction.
 */

export type TrackerState = 'done' | 'current' | 'future' | 'exception';

/** A detail line under a milestone: an amount, an outcome, a second signature. */
export interface TrackerNote {
  label: string;
  at?: string;
  txHash?: string | null;
}

export interface TrackerStep {
  key: string;
  title: string;
  /** Plain-language description of the step. Shown under the title. */
  description: string;
  state: TrackerState;
  /** When it happened — present for `done` and `exception`, absent for `current` and `future`. */
  at: string | null;
  txHash: string | null;
  /** `current` only: what has to happen next, and who has to do it. */
  waitingFor?: string;
  notes: TrackerNote[];
}

export interface TrackerHeadline {
  /** The status in the buyer's words: "Payment secured in escrow", never "escrowed". */
  status: string;
  /** The date that matters now — an ETA, a deadline, or what we are waiting for. */
  detail: string | null;
  tone: 'neutral' | 'progress' | 'success' | 'danger';
}

/**
 * What the tracker component renders. Both verticals build this same shape — the delivery
 * milestones here, the rental ones in `rentalTracker.ts` — so one component draws both and the
 * two can never drift into two different-looking trackers.
 */
export interface DealTrackerModel {
  headline: TrackerHeadline;
  steps: TrackerStep[];
}

/** The delivery tracker is that model; the name is kept where delivery code already reads it. */
export type DeliveryTracker = DealTrackerModel;

/** The six milestones of a delivery deal, in order. */
const MILESTONES: { key: string; title: string; description: string; step: DealStep }[] = [
  {
    key: 'offer',
    title: 'Offer placed',
    description: 'You asked the dealer to sell and deliver this car.',
    step: DealStep.DeliveryOfferCreated,
  },
  {
    key: 'approved',
    title: 'Dealer confirmed',
    description: 'The dealer accepted and quoted the delivery.',
    step: DealStep.DeliveryApproved,
  },
  {
    key: 'escrowed',
    title: 'Payment secured',
    description: 'Your money is held by the contract until the car is collected.',
    step: DealStep.DeliveryEscrowed,
  },
  {
    key: 'picked_up',
    title: 'Picked up',
    description: 'The logist collected the car from the dealer.',
    step: DealStep.DeliveryPickedUp,
  },
  {
    key: 'delivered',
    title: 'Ready for handover',
    description: 'The car is at your pickup point.',
    step: DealStep.DeliveryDelivered,
  },
  {
    key: 'handed_over',
    title: 'Completed',
    description: 'You collected the car with your handover code.',
    step: DealStep.DeliveryHandedOver,
  },
];

/**
 * What has to happen for **this** milestone to be reached, and who has to do it.
 *
 * Keyed by the step it belongs to, not by the step before it: the current milestone is the one
 * that has *not* happened yet, so its line describes the thing still owed. Getting this off by one
 * puts "Waiting for your payment" under "Dealer confirmed" on a deal the dealer has not answered.
 */
const WAITING_FOR: Record<string, string> = {
  offer: 'Sending your offer to the dealer',
  approved: 'Waiting for the dealer to confirm and quote the delivery',
  escrowed: 'Waiting for your payment',
  picked_up: 'Waiting for the logist to collect the car from the dealer',
  delivered: 'In transit — waiting for the logist to deliver the car',
  handed_over: 'Waiting for you to collect the car and give your handover code',
};

/** Statuses that end the deal without the car changing hands — each with its own copy and colour. */
const EXCEPTIONS: Partial<Record<DeliveryDealStatus, { title: string; description: string }>> = {
  [DeliveryDealStatus.Cancelled]: {
    title: 'Cancelled — refund issued',
    description: 'You cancelled the order and the escrow returned the car price and the delivery.',
  },
  [DeliveryDealStatus.Refunded]: {
    title: 'Dispute resolved — refund issued',
    description: 'The escrow returned the car price and the delivery to you.',
  },
  [DeliveryDealStatus.Withdrawn]: {
    title: 'Offer withdrawn',
    description: 'You withdrew the offer before any money moved.',
  },
  [DeliveryDealStatus.Declined]: {
    title: 'Offer declined',
    description: 'The dealer declined this offer.',
  },
  [DeliveryDealStatus.OfferExpired]: {
    title: 'Offer expired',
    description: 'The dealer did not answer in time. No money moved.',
  },
  [DeliveryDealStatus.ApprovalExpired]: {
    title: 'Payment window expired',
    description: 'The payment window closed before the payment arrived. The car is back on the showcase.',
  },
};

/** Headline copy per live status — the same fact the tracker shows, said in one line. */
const HEADLINES: Record<DeliveryDealStatus, { status: string; tone: TrackerHeadline['tone'] }> = {
  [DeliveryDealStatus.Offered]: { status: 'Waiting for the dealer', tone: 'progress' },
  [DeliveryDealStatus.Approved]: { status: 'Ready to pay', tone: 'progress' },
  [DeliveryDealStatus.AwaitingPayment]: { status: 'Ready to pay', tone: 'progress' },
  [DeliveryDealStatus.Escrowed]: { status: 'Payment secured in escrow', tone: 'success' },
  [DeliveryDealStatus.Disputed]: { status: 'Dispute opened', tone: 'danger' },
  [DeliveryDealStatus.PickedUp]: { status: 'On its way to you', tone: 'progress' },
  [DeliveryDealStatus.Delivered]: { status: 'Ready for handover', tone: 'success' },
  [DeliveryDealStatus.HandedOver]: { status: 'Completed', tone: 'success' },
  [DeliveryDealStatus.OfferExpired]: { status: 'Offer expired', tone: 'neutral' },
  [DeliveryDealStatus.Declined]: { status: 'Offer declined', tone: 'neutral' },
  [DeliveryDealStatus.Withdrawn]: { status: 'Offer withdrawn', tone: 'neutral' },
  [DeliveryDealStatus.ApprovalExpired]: { status: 'Payment window expired', tone: 'danger' },
  [DeliveryDealStatus.Cancelled]: { status: 'Cancelled — refund issued', tone: 'neutral' },
  [DeliveryDealStatus.Refunded]: { status: 'Refund issued', tone: 'neutral' },
};

const first = (events: ApiDealStepEvent[], step: DealStep): ApiDealStepEvent | undefined =>
  events.find((event) => event.step === step);

const allOf = (events: ApiDealStepEvent[], step: DealStep): ApiDealStepEvent[] =>
  events.filter((event) => event.step === step);

/**
 * One amount of an event, formatted for a note line — shared with the rental tracker.
 *
 * Two shapes arrive because two writers exist: an on-chain step carries base units as strings
 * (the contract's own integers), a platform step carries the display amount api already quoted.
 * Both land on the same formatter, so a tracker line and an invoice line can never disagree
 * about what "18,348" means.
 */
export const trackerAmount = (value: unknown, token: ApiChainToken | null): string | null => {
  if (typeof value === 'number') {
    return `${formatAmount(value)}${token ? ` ${token.symbol}` : ''}`;
  }
  if (typeof value === 'string' && token) {
    return formatBaseUnits(value, token.decimals, token.symbol);
  }

  return null;
};

/** The money notes of one delivery step, read out of the event's own `details`. */
function notesOf(event: ApiDealStepEvent, token: ApiChainToken | null): TrackerNote[] {
  const details = event.details ?? {};
  const notes: TrackerNote[] = [];
  const add = (label: string | null) => {
    if (label) notes.push({ label });
  };
  // The three refund exits (cancel, resolution, stale claim) move the same money and say so the
  // same way; a row harvested before the api named the amount keeps the sentence without it.
  const refundNote = (prefix: string) => withAmount(prefix, trackerAmount(details.refundAmount, token)) ?? prefix;
  // A payout is the dealer's price and the logist's delivery — the pickup outcome, whichever
  // event executed it. Two notes, as the contract makes two transfers.
  const payoutNotes = (dealerPrefix: string) => {
    add(withAmount(dealerPrefix, trackerAmount(details.dealerAmount, token)) ?? dealerPrefix);
    add(withAmount('Logist paid', trackerAmount(details.logistAmount, token)));
  };

  switch (event.step) {
    case DealStep.DeliveryApproved:
      add(withAmount('Delivery quoted', trackerAmount(details.deliveryAmount, token)));
      break;
    case DealStep.DeliveryEscrowed:
      add(withAmount('Paid into escrow', trackerAmount(details.totalPaid, token)));
      add(withAmount('Platform fee taken', trackerAmount(details.feePaid, token)));
      break;
    case DealStep.DeliveryPickedUp:
      payoutNotes('Dealer paid');
      break;
    case DealStep.DeliveryCancelled:
      add(refundNote('Refund issued'));
      break;
    case DealStep.DeliveryResolutionSigned:
      add(`Resolution signed — ${details.refundToBuyer ? 'refund to you' : 'payout to the dealer'}`);
      break;
    case DealStep.DeliveryResolved:
      // The outcome names its money when the api harvested it (api#188): a refund is price +
      // delivery back to the buyer, a payout pays the dealer and the logist like a pickup.
      if (details.refundToBuyer) add(refundNote('Dispute resolved — refund issued to you'));
      else payoutNotes('Dispute resolved — the dealer was paid');
      break;
    case DealStep.DeliveryStaleDisputeClaimed:
      // The same refund as a resolution, taken by the buyer alone after the 14-day timeout.
      add(refundNote('Refund claimed after the 14-day dispute timeout'));
      break;
    default:
      break;
  }

  return notes;
}

/** "Refund issued — 18,080 USDC", or nothing at all when the event carried no amount. */
export const withAmount = (prefix: string, amount: string | null): string | null =>
  amount ? `${prefix} — ${amount}` : null;

/**
 * Milestones, in order, each one carrying its own time and whatever money it moved.
 *
 * A step is `done` when its event exists — not when the status looks far enough along. The two
 * agree in normal life and disagree exactly where it matters: a deal that jumped straight to a
 * terminal status never earned the steps in between, and showing them ticked would be a lie with
 * a timestamp on it.
 */
function buildSteps(deal: ApiDeliveryDealDetail, events: ApiDealStepEvent[], token: ApiChainToken | null): TrackerStep[] {
  const exception = EXCEPTIONS[deal.status];
  const steps: TrackerStep[] = [];
  let currentTaken = false;

  MILESTONES.forEach((milestone) => {
    const event = first(events, milestone.step);

    if (event) {
      steps.push({
        key: milestone.key,
        title: milestone.title,
        description: milestone.description,
        state: 'done',
        at: event.occurredAt,
        txHash: event.txHash,
        notes: notesOf(event, token),
      });

      return;
    }

    // The first milestone without an event is where the deal stands right now — unless the deal
    // ended off the happy path, in which case there is no "current" step at all: the exception
    // below is the last thing that happened.
    const isCurrent = !currentTaken && !exception;
    currentTaken = currentTaken || isCurrent;

    steps.push({
      key: milestone.key,
      title: milestone.title,
      description: milestone.description,
      state: isCurrent ? 'current' : 'future',
      at: null,
      txHash: null,
      waitingFor: isCurrent ? WAITING_FOR[milestone.key] : undefined,
      notes: [],
    });
  });

  return steps;
}

/**
 * Steps that only exist when something went sideways: the dispute and the ending that replaced
 * the happy path. They are appended after the milestones, in the order they happened.
 */
function buildExceptionSteps(
  deal: ApiDeliveryDealDetail,
  events: ApiDealStepEvent[],
  token: ApiChainToken | null
): TrackerStep[] {
  const steps: TrackerStep[] = [];
  const raised = first(events, DealStep.DeliveryDisputeRaised);

  if (raised) {
    const signatures = allOf(events, DealStep.DeliveryResolutionSigned);
    const resolved = first(events, DealStep.DeliveryResolved);
    const claimed = first(events, DealStep.DeliveryStaleDisputeClaimed);
    const closing = resolved ?? claimed;

    steps.push({
      key: 'dispute',
      title: closing ? 'Dispute resolved' : 'Dispute opened',
      description: closing
        ? 'Two of the three parties signed the same outcome, and the contract executed it.'
        : 'Pickup and cancellation are paused until two of the three parties sign the same outcome.',
      state: 'exception',
      at: raised.occurredAt,
      txHash: raised.txHash,
      waitingFor: closing ? undefined : 'Waiting for signatures — two of you, the dealer and GAC',
      notes: [
        ...signatures.flatMap((event) =>
          notesOf(event, token).map((note) => ({ ...note, at: event.occurredAt, txHash: event.txHash }))
        ),
        ...(closing
          ? notesOf(closing, token).map((note) => ({ ...note, at: closing.occurredAt, txHash: closing.txHash }))
          : []),
      ],
    });
  }

  const exception = EXCEPTIONS[deal.status];

  if (exception) {
    const ending =
      first(events, DealStep.DeliveryCancelled) ??
      first(events, DealStep.DeliveryOfferWithdrawn) ??
      first(events, DealStep.DeliveryOfferDeclined) ??
      first(events, DealStep.DeliveryOfferExpired) ??
      first(events, DealStep.DeliveryApprovalExpired);

    steps.push({
      key: 'ending',
      title: exception.title,
      description: exception.description,
      state: 'exception',
      at: ending?.occurredAt ?? deal.closedAt ?? null,
      txHash: ending?.txHash ?? null,
      notes: ending ? notesOf(ending, token) : [],
    });
  }

  return steps;
}

/**
 * The header's second line: the date the buyer is actually waiting on.
 *
 * Priority is "what is closest to me": an open window's deadline outranks a delivery estimate,
 * because it is the one that costs the deal if missed; a signature we are waiting for outranks
 * both, because nothing moves without it.
 */
function buildDetail(deal: ApiDeliveryDealDetail): string | null {
  if (deal.status === DeliveryDealStatus.Disputed) {
    return 'Waiting for signatures — two of the three parties must sign the same outcome';
  }

  if (deal.status === DeliveryDealStatus.Offered) {
    return `Offer expires ${formatDateTime(deal.offerExpiresAt)}`;
  }

  if (deal.status === DeliveryDealStatus.Approved || deal.status === DeliveryDealStatus.AwaitingPayment) {
    const deadline = deal.paymentExpiresAt ?? deal.offerExpiresAt;

    return `Pay before ${formatDateTime(deadline)}`;
  }

  if (deal.estimatedDeliveryAt && (deal.status === DeliveryDealStatus.Escrowed || deal.status === DeliveryDealStatus.PickedUp)) {
    return `Estimated delivery ${formatUtcDay(deal.estimatedDeliveryAt)}`;
  }

  if (deal.status === DeliveryDealStatus.Escrowed) {
    return 'The logist will name a delivery date shortly';
  }

  if (deal.status === DeliveryDealStatus.Delivered) {
    return `Waiting for you at ${deal.citySnapshot}`;
  }

  return null;
}

export function buildDeliveryTracker(deal: ApiDeliveryDealDetail, chain: DealChain | null): DeliveryTracker {
  const token = chain?.token ?? null;
  // api returns them oldest-first; sorting here keeps the tracker honest against any order.
  const events = [...(deal.stepEvents ?? [])].sort(
    (left, right) => new Date(left.occurredAt).getTime() - new Date(right.occurredAt).getTime()
  );

  return {
    headline: {
      ...HEADLINES[deal.status],
      detail: buildDetail(deal),
    },
    steps: [...buildSteps(deal, events, token), ...buildExceptionSteps(deal, events, token)],
  };
}

